import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { UserPlus, RefreshCw, Trash2, Eye, UserCheck, User, Shield, Edit3, Handshake, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminUsers = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRole, setFilterRole] = useState('all');

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}users.php`);
            const data = await response.json();
            setUsersList(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error(t('admin.users.toastLoadError') || 'მომხმარებლების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user) fetchUsers();
    }, [user, fetchUsers]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            const response = await fetchWithSecurity(`${API_URL}users.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_role', id: userId, role: newRole })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('admin.users.toastRoleUpdated') || 'მომხმარებლის როლი განახლდა');
                fetchUsers();
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error(t('admin.users.toastRoleError') || 'როლის განახლება ვერ მოხერხდა');
        }
    };

    const deleteUser = async (userId) => {
        if (window.confirm(t('admin.users.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ მომხმარებლის წაშლა?')) {
            try {
                const response = await fetchWithSecurity(`${API_URL}users.php?id=${userId}`, { method: 'DELETE' });
                const result = await response.json();
                if (result.success) {
                    toast.success(t('admin.users.toastDeleted') || 'მომხმარებელი წაიშალა');
                    fetchUsers();
                } else {
                    toast.error(result.error);
                }
            } catch (error) {
                toast.error(t('admin.users.toastDeleteError') || 'წაშლა ვერ მოხერხდა');
            }
        }
    };

    if (authLoading || !user) return null;

    const getRoleLabel = (role) => {
        switch (role) {
            case 'super_admin': return t('roles.super_admin') || 'სუპერ ადმინი';
            case 'partner': return t('roles.partner') || 'ავტორიზებული პარტნიორი';
            case 'blog_manager': return t('roles.blog_manager') || 'კონტენტის მენეჯერი';
            case 'client': return t('roles.client') || 'კლიენტი';
            default: return role;
        }
    };

    const roleKpis = [
        { key: 'all',         label: t('admin.users.roleAll') || 'ყველა',          icon: Users,     color: '#6366f1', bg: 'rgba(99,102,241,0.1)',  count: usersList.length },
        { key: 'super_admin', label: t('roles.super_admin') || 'სუპერ ადმინი',   icon: Shield,    color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)',  count: usersList.filter(u => u.role === 'super_admin').length },
        { key: 'blog_manager',label: t('roles.blog_manager') || 'ბლოგ მენეჯერი', icon: Edit3,     color: '#a855f7', bg: 'rgba(168,85,247,0.1)',  count: usersList.filter(u => u.role === 'blog_manager').length },
        { key: 'partner',     label: t('roles.partner') || 'პარტნიორი',           icon: Handshake, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  count: usersList.filter(u => u.role === 'partner').length },
        { key: 'client',      label: t('roles.client') || 'კლიენტი',              icon: User,      color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   count: usersList.filter(u => u.role === 'client').length },
    ];

    const filteredUsers = filterRole === 'all' ? usersList : usersList.filter(u => u.role === filterRole);

    return (
        <AdminLayout title={t('admin.users.layoutTitle') || 'მომხმარებლები'}>
            {/* Role KPI Cards */}
            <div className="row g-3 mb-4">
                {roleKpis.map(kpi => {
                    const KIcon = kpi.icon;
                    const isActive = filterRole === kpi.key;
                    return (
                        <div className="col-6 col-md-4 col-lg" key={kpi.key}>
                            <div
                                className="shadcn-card p-3 text-center cursor-pointer"
                                style={{
                                    border: isActive ? `2px solid ${kpi.color}` : '1.5px solid var(--admin-border)',
                                    background: isActive ? kpi.bg : '#fff',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onClick={() => setFilterRole(kpi.key)}
                            >
                                <div className="d-flex justify-content-center mb-2">
                                    <div style={{ background: kpi.bg, borderRadius: '10px', padding: '8px', color: kpi.color }}>
                                        <KIcon size={20} />
                                    </div>
                                </div>
                                <div className="fw-bold fs-4 text-dark">{kpi.count}</div>
                                <div className="text-muted small">{kpi.label}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                    <div className="shadcn-card-title">
                        <UserCheck size={20} className="text-indigo" />
                        <span>{t('admin.users.registryTitle') || 'სისტემური მომხმარებლები'} ({filteredUsers.length})</span>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchUsers} title={t('common.refresh') || 'განახლება'}>
                            <RefreshCw size={14} />
                        </button>
                        <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => navigate('/admin/users/add')}>
                            <UserPlus size={14} />
                            <span>{t('admin.users.addNew') || 'ახალი მომხმარებელი'}</span>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.users.loading') || 'მონაცემები იტვირთება...'}</span>
                    </div>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{t('admin.users.colProfile') || 'მომხმარებელი'}</th>
                                    <th>{t('admin.users.colRole') || 'როლი (RBAC)'}</th>
                                    <th>{t('admin.users.colRegDate') || 'რეგისტრაციის თარიღი'}</th>
                                    <th className="text-end">{t('common.actions') || 'მოქმედებები'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(u => (
                                    <tr key={u.id}>
                                        <td><span className="text-muted">#{u.id}</span></td>
                                        <td>
                                            <Link to={`/admin/users/detail/${u.id}`} className="d-flex align-items-center gap-3 text-decoration-none">
                                                {u.profile_picture ? (
                                                    <img
                                                        src={u.profile_picture.startsWith('http') ? u.profile_picture : API_URL + u.profile_picture}
                                                        alt=""
                                                        className="avatar-circle"
                                                    />
                                                ) : (
                                                    <div className="avatar-circle-icon">
                                                        <User size={18} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="fw-bold text-dark">{u.username}</div>
                                                    {u.username === 'admin' && (
                                                        <span className="shadcn-badge shadcn-badge-danger">{t('admin.users.rootAdmin') || 'მთავარი ადმინი'}</span>
                                                    )}
                                                </div>
                                            </Link>
                                        </td>
                                        <td>
                                            {u.id !== user.id && u.username !== 'admin' ? (
                                                <select
                                                    className="shadcn-select form-select-sm py-1"
                                                    style={{ maxWidth: '180px' }}
                                                    value={u.role}
                                                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                                >
                                                    <option value="partner">{t('roles.partner') || 'პარტნიორი'}</option>
                                                    <option value="blog_manager">{t('roles.blog_manager') || 'კონტენტის მენეჯერი'}</option>
                                                    <option value="client">{t('roles.client') || 'კლიენტი'}</option>
                                                    <option value="super_admin">{t('roles.super_admin') || 'სუპერ ადმინი'}</option>
                                                </select>
                                            ) : (
                                                <span className="shadcn-badge shadcn-badge-primary">{getRoleLabel(u.role)}</span>
                                            )}
                                        </td>
                                        <td className="small text-muted">{new Date(u.created_at).toLocaleDateString()}</td>
                                        <td className="text-end">
                                            <div className="d-flex gap-2 justify-content-end">
                                                <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/users/detail/${u.id}`)} title={t('common.details') || 'დეტალები'}>
                                                    <Eye size={14} /> {t('common.details') || 'დეტალები'}
                                                </button>
                                                {u.id !== user.id && u.username !== 'admin' && (
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteUser(u.id)} title={t('common.delete') || 'წაშლა'}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminUsers;
